import { TooltipWrapper } from 'components/TooltipWrapper'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import {
  ConsequenceMatrixConfig,
  ConsequenceMatrixSelection,
  DEFAULT_RISK_BACKGROUND_COLORS,
  DEFAULT_RISK_COLORS,
  GeckoRiskAssessmentConfig,
  type RiskMatrixLabel,
  RiskMatrixOrientation,
} from 'types'

import {
  FALLBACK_CONSEQUENCE_LABELS,
  FALLBACK_LIKELIHOOD_LABELS,
  FALLBACK_RISK_MATRIX,
} from './riskMatrixDefaults'
import {
  calculateLikelihoodConsequence,
  calculateMatrixPosition,
} from './riskMatrixHelpers'

const TOOLTIP_Z_INDEX = 10_020

const MatrixOuterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
`

const RowAxisTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-right: 4px;
`

const MatrixInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ColumnAxisTitle = styled.div<{ $labelWidth: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-bottom: 4px;
  padding-left: ${({ $labelWidth }) => $labelWidth}px;
`

const RiskMatrixContainer = styled.div<{
  $numColumns: number
  $numRows: number
  $cellSize: number
  $labelWidth: number
  $gap: number
}>`
  display: grid;
  grid-template-columns: ${({ $labelWidth }) => $labelWidth}px repeat(
      ${({ $numColumns }) => $numColumns},
      1fr
    );
  grid-template-rows: auto repeat(${({ $numRows }) => $numRows}, 1fr);
  gap: ${({ $gap }) => $gap}px;
  max-width: ${({ $numColumns, $cellSize, $labelWidth, $gap }) =>
    $labelWidth + $numColumns * $cellSize + ($numColumns + 1) * $gap}px;
`

const MatrixLabel = styled.div<{
  $labelFontSize: number
  $labelHeight: number
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $labelFontSize }) => $labelFontSize}px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  min-height: ${({ $labelHeight }) => $labelHeight}px;
  text-align: center;
`

const ConsequenceLabelBox = styled.div<{
  $variant: 'max' | 'other'
  $boxSize: number
  $labelFontSize: number
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $boxSize }) => $boxSize}px;
  height: ${({ $boxSize }) => $boxSize}px;
  border-radius: 5px;
  font-size: ${({ $labelFontSize }) => $labelFontSize}px;
  font-weight: 600;
  cursor: default;
  color: ${({ $variant }) => ($variant === 'max' ? '#4ade80' : '#aaa')};
  background: ${({ $variant }) =>
    $variant === 'max' ? 'rgba(74,222,128,0.12)' : 'rgba(255,255,255,0.06)'};
  box-shadow: ${({ $variant }) =>
    $variant === 'max'
      ? 'inset 0 0 0 1px rgba(74,222,128,0.3)'
      : 'inset 0 0 0 1px rgba(255,255,255,0.12)'};
`

const TooltipBlock = styled.span`
  display: block;
  font-size: 12px;
  line-height: 1.4;
  max-width: 280px;
`

const TooltipSep = styled.span`
  display: block;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  margin: 5px 0;
`

const TooltipMeta = styled.span`
  display: block;
  font-size: 11px;
  opacity: 0.6;
`

const MatrixCell = styled.div<{
  $color: string
  $selected?: boolean
  $backgroundColor: string
  $readOnly?: boolean
  $dimmed?: boolean
  $cellSize: number
  $cellFontSize: number
}>`
  width: ${({ $cellSize }) => $cellSize}px;
  height: ${({ $cellSize }) => $cellSize}px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.forecast.secondaryText : 'transparent'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $cellFontSize }) => $cellFontSize}px;
  font-weight: bold;
  color: ${({ $color }) => $color};
  cursor: ${({ $readOnly, $dimmed }) =>
    $readOnly || $dimmed ? 'default' : 'pointer'};
  transition: border-color 0.2s;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.35 : 1)};

  ${({ $readOnly, $dimmed, theme }) =>
    !$readOnly &&
    !$dimmed &&
    `
    &:hover {
      border-color: ${theme.forecast.secondaryText};
    }
  `}
`

/** Tooltip body listing the consequence-matrix selections at one axis label. */
function ConsequenceSelectionTooltip({
  selections,
  config,
}: {
  selections: ConsequenceMatrixSelection[]
  config: ConsequenceMatrixConfig
}) {
  return (
    <TooltipBlock>
      {selections.map((sel, i) => {
        const rowIdx = config.rowLabels.indexOf(sel.selectedRowValue)
        const cellText = config.matrix[rowIdx]?.[sel.columnIndex] ?? ''
        return (
          <span key={sel.columnIndex}>
            {i > 0 && <TooltipSep />}
            <span style={{ fontWeight: 600 }}>{sel.columnTitle}</span>
            {cellText && (
              <span style={{ display: 'block', marginTop: 2 }}>{cellText}</span>
            )}
            <TooltipMeta>
              {sel.userName} &middot;{' '}
              {new Date(sel.timestamp).toLocaleDateString()}
            </TooltipMeta>
          </span>
        )
      })}
    </TooltipBlock>
  )
}

interface RiskMatrixProps {
  riskAssessmentConfig?: GeckoRiskAssessmentConfig
  selectedLikelihood?: number
  selectedConsequence?: number
  /** When set, only cells matching this consequence are interactive; others are dimmed. */
  lockedConsequence?: number
  onCellClick: (
    riskValue: string,
    likelihood: number,
    consequence: number
  ) => void
  readOnly?: boolean
  /** When provided, overlays consequence selection indicators on the consequence axis labels. */
  consequenceMatrixSelections?: ConsequenceMatrixSelection[]
  consequenceMatrixConfig?: ConsequenceMatrixConfig
  /** Cell size in pixels. Defaults to 42. All other dimensions scale proportionally. */
  cellSize?: number
}

const DEFAULT_CELL_SIZE = 42

export const RiskMatrix: React.FC<RiskMatrixProps> = ({
  riskAssessmentConfig,
  selectedLikelihood,
  selectedConsequence,
  lockedConsequence,
  onCellClick,
  readOnly = false,
  consequenceMatrixSelections,
  consequenceMatrixConfig,
  cellSize = DEFAULT_CELL_SIZE,
}) => {
  const scale = cellSize / DEFAULT_CELL_SIZE
  const labelWidth = Math.round(80 * scale)
  const gap = Math.max(2, Math.round(4 * scale))
  const labelFontSize = Math.max(8, Math.round(12 * scale))
  const labelHeight = Math.round(32 * scale)
  const cellFontSize = Math.round(15 * scale)
  const boxSize = Math.round(28 * scale)
  const consequenceLabels =
    riskAssessmentConfig?.data.consequenceLabels ?? FALLBACK_CONSEQUENCE_LABELS
  const likelihoodLabels =
    riskAssessmentConfig?.data.likelihoodLabels ?? FALLBACK_LIKELIHOOD_LABELS
  const riskMatrix =
    riskAssessmentConfig?.data.riskMatrix ?? FALLBACK_RISK_MATRIX
  const riskColors =
    riskAssessmentConfig?.data.riskColors ?? DEFAULT_RISK_COLORS
  const riskBackgroundColors =
    riskAssessmentConfig?.data.riskBackgroundColors ??
    DEFAULT_RISK_BACKGROUND_COLORS
  const orientation =
    riskAssessmentConfig?.data.riskMatrixOrientation ??
    RiskMatrixOrientation.LIKELIHOOD_AS_ROWS

  const isLikelihoodAsRows =
    orientation === RiskMatrixOrientation.LIKELIHOOD_AS_ROWS

  const rowLabels = isLikelihoodAsRows ? likelihoodLabels : consequenceLabels
  const columnLabels = isLikelihoodAsRows ? consequenceLabels : likelihoodLabels

  const numRows = riskMatrix.length || 5
  const numColumns = riskMatrix[0]?.length || 5

  const selectedPosition =
    selectedLikelihood !== undefined && selectedConsequence !== undefined
      ? calculateMatrixPosition(
          selectedLikelihood,
          selectedConsequence,
          isLikelihoodAsRows
        )
      : null

  const lockedConsequenceIndex =
    lockedConsequence !== undefined ? lockedConsequence - 1 : undefined

  // Build a map from consequence position (0-indexed col) → selections at that level
  const selectionsByConsequenceIndex = useMemo(() => {
    if (!consequenceMatrixSelections || !consequenceMatrixConfig) return null
    const map = new Map<number, ConsequenceMatrixSelection[]>()
    for (const sel of consequenceMatrixSelections) {
      const colIdx = consequenceMatrixConfig.rowLabels.indexOf(
        sel.selectedRowValue
      )
      if (colIdx === -1) continue
      const arr = map.get(colIdx) ?? []
      arr.push(sel)
      map.set(colIdx, arr)
    }
    return map
  }, [consequenceMatrixSelections, consequenceMatrixConfig])

  const maxConsequenceIndex = useMemo(() => {
    if (
      !consequenceMatrixSelections ||
      consequenceMatrixSelections.length === 0
    )
      return null
    if (!consequenceMatrixConfig) return null
    const maxVal = Math.max(
      ...consequenceMatrixSelections.map((s) => s.selectedRowValue)
    )
    return consequenceMatrixConfig.rowLabels.indexOf(maxVal)
  }, [consequenceMatrixSelections, consequenceMatrixConfig])

  const handleCellClick = (row: number, col: number) => {
    if (readOnly) return
    if (lockedConsequenceIndex !== undefined) {
      const consequenceIndex = isLikelihoodAsRows ? col : row
      if (consequenceIndex !== lockedConsequenceIndex) return
    }
    const riskValue = riskMatrix[row][col]
    const { likelihood, consequence } = calculateLikelihoodConsequence(
      { row, col },
      isLikelihoodAsRows
    )
    onCellClick(riskValue, likelihood, consequence)
  }

  const rowAxisLabel = isLikelihoodAsRows ? 'Likelihood' : 'Consequence'
  const columnAxisLabel = isLikelihoodAsRows ? 'Consequence' : 'Likelihood'

  // Renders a consequence/likelihood axis label, overlaying selection
  // indicators + tooltip when this axis carries consequence-matrix selections.
  // `side` is the only difference between the column ('top') and row ('left') axes.
  const renderConsequenceAxisLabel = (
    label: RiskMatrixLabel,
    cmIndex: number | null,
    side: 'top' | 'left'
  ) => {
    const cmSelections =
      cmIndex !== null ? selectionsByConsequenceIndex?.get(cmIndex) : null
    const isMax = cmIndex !== null && cmIndex === maxConsequenceIndex

    if (cmSelections && cmSelections.length > 0 && consequenceMatrixConfig) {
      return (
        <MatrixLabel $labelFontSize={labelFontSize} $labelHeight={labelHeight}>
          <TooltipWrapper
            label={
              <ConsequenceSelectionTooltip
                selections={cmSelections}
                config={consequenceMatrixConfig}
              />
            }
            side={side}
            offset={8}
            zIndex={TOOLTIP_Z_INDEX}>
            <ConsequenceLabelBox
              $variant={isMax ? 'max' : 'other'}
              $boxSize={boxSize}
              $labelFontSize={labelFontSize}>
              {label.value}
            </ConsequenceLabelBox>
          </TooltipWrapper>
        </MatrixLabel>
      )
    }

    return label.description ? (
      <TooltipWrapper label={label.description} zIndex={TOOLTIP_Z_INDEX}>
        <MatrixLabel $labelFontSize={labelFontSize} $labelHeight={labelHeight}>
          {label.value}
        </MatrixLabel>
      </TooltipWrapper>
    ) : (
      <MatrixLabel $labelFontSize={labelFontSize} $labelHeight={labelHeight}>
        {label.value}
      </MatrixLabel>
    )
  }

  return (
    <MatrixOuterContainer>
      <RowAxisTitle>{rowAxisLabel}</RowAxisTitle>
      <MatrixInnerContainer>
        <ColumnAxisTitle $labelWidth={labelWidth}>
          {columnAxisLabel}
        </ColumnAxisTitle>
        <RiskMatrixContainer
          $numColumns={numColumns}
          $numRows={numRows}
          $cellSize={cellSize}
          $labelWidth={labelWidth}
          $gap={gap}>
          <div></div>
          {columnLabels.map((label, colIndex) => (
            // Consequence is along the column axis when isLikelihoodAsRows
            <React.Fragment key={label.value}>
              {renderConsequenceAxisLabel(
                label,
                isLikelihoodAsRows ? colIndex : null,
                'top'
              )}
            </React.Fragment>
          ))}
          {rowLabels.map((label, rowIndex) => {
            // Consequence is along the row axis when !isLikelihoodAsRows
            const cmIndex = !isLikelihoodAsRows ? rowIndex : null

            return (
              <React.Fragment key={label.value}>
                {renderConsequenceAxisLabel(label, cmIndex, 'left')}
                {riskMatrix[rowIndex].map((value, colIndex) => {
                  const consequenceIndex = isLikelihoodAsRows
                    ? colIndex
                    : rowIndex
                  const isDimmed =
                    lockedConsequenceIndex !== undefined &&
                    consequenceIndex !== lockedConsequenceIndex
                  return (
                    <MatrixCell
                      key={`${rowIndex}-${colIndex}`}
                      $color={riskColors[value as keyof typeof riskColors]}
                      $backgroundColor={
                        riskBackgroundColors[
                          value as keyof typeof riskBackgroundColors
                        ]
                      }
                      $selected={
                        selectedPosition?.row === rowIndex &&
                        selectedPosition?.col === colIndex
                      }
                      $readOnly={readOnly}
                      $dimmed={isDimmed}
                      $cellSize={cellSize}
                      $cellFontSize={cellFontSize}
                      onClick={() => handleCellClick(rowIndex, colIndex)}>
                      {value}
                    </MatrixCell>
                  )
                })}
              </React.Fragment>
            )
          })}
        </RiskMatrixContainer>
      </MatrixInnerContainer>
    </MatrixOuterContainer>
  )
}
